import { DollarSign, Clock, MailSearch } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const problems = [
  {
    icon: DollarSign,
    text: "CRMs are expensive and complex",
    description: "Salesforce, HubSpot, and Pipedrive cost hundreds per month and require extensive training.",
  },
  {
    icon: Clock,
    text: "Manual contact work steals your time",
    description: "Copy-pasting names, companies, and phone numbers from emails takes hours every week.",
  },
  {
    icon: MailSearch,
    text: "Your contacts live in email threads",
    description: "Important people get buried in your inbox with no structure, no tags, no context.",
  },
];

const ProblemCard = ({ problem, index }: { problem: typeof problems[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: 'slide-up',
    delay: index * 150,
    threshold: 0.2,
  });

  return (
    <div 
      ref={ref}
      className={`bg-card rounded-xl p-6 md:p-8 shadow-card border border-border group hover:shadow-elevated transition-all duration-300 ${animationClass}`}
    >
      <div className="mb-4 inline-flex p-3 rounded-lg bg-destructive/10 group-hover:animate-wiggle">
        <problem.icon className="w-6 h-6 text-destructive" />
      </div>
      <h3 className="text-lg md:text-xl font-bold mb-3 text-foreground">
        {problem.text}
      </h3>
      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
        {problem.description}
      </p>
    </div>
  );
};

export const Problem = () => {
  const { ref: titleRef, animationClass: titleAnimation } = useScrollAnimation<HTMLDivElement>({
    type: 'fade-scale',
    threshold: 0.3,
  });

  return (
    <section className="py-16 md:py-24 bg-muted/30">
      <div className="container px-4">
        <div className="max-w-4xl mx-auto">
          <div 
            ref={titleRef}
            className={`text-center mb-8 md:mb-12 ${titleAnimation}`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 md:mb-6">
              Not everyone needs a full CRM.
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              Heavy CRM platforms are overkill for most professionals. You don't need pipelines, sales stages, or 50-page manuals. You just need smart contact management in Outlook — the tool you already use every day.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {problems.map((problem, index) => (
              <ProblemCard key={index} problem={problem} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
